import type { HttpAdapter, HttpRequest, HttpResponse } from '../client';
import { RequestTimeoutError } from '../errors';

// 兼容旧的导入路径（原先定义在本文件），新代码请从包根或 `../errors` 引入
export { RequestTimeoutError } from '../errors';

export interface FetchAdapterOptions {
  /** 注入自定义 fetch（Node <18、测试桩、RN 的 whatwg-fetch 等）。 */
  fetchImpl?: typeof fetch;
  /**
   * 单次请求超时（毫秒），默认 30s。
   * 不设超时时，弱网/半开连接会让 fetch 永久 pending：界面一直转圈，
   * 离线队列也永远等不到 reject 而无法入队（等于静默卡单）。传 0 关闭超时。
   */
  timeoutMs?: number;
}

/**
 * 基于 fetch 的适配器：Web / RN / Taro-H5 直接可用。
 * 小程序端请另写 `Taro.request` 适配器（同样的 HttpAdapter 接口）。
 */
export function createFetchAdapter(options: FetchAdapterOptions = {}): HttpAdapter {
  const timeoutMs = options.timeoutMs ?? 30_000;

  const send = async (request: HttpRequest): Promise<HttpResponse> => {
    const fetchImpl = options.fetchImpl ?? globalThis.fetch;
    if (typeof fetchImpl !== 'function') {
      throw new Error('当前运行环境没有 fetch，请注入 fetchImpl 或提供自定义适配器');
    }

    // 组合「调用方 signal」与「超时」：任一触发都要中断底层请求
    const controller = new AbortController();
    let timedOut = false;
    const timer =
      timeoutMs > 0
        ? setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, timeoutMs)
        : null;

    const forwardAbort = () => controller.abort();
    if (request.signal) {
      if (request.signal.aborted) controller.abort();
      else request.signal.addEventListener('abort', forwardAbort);
    }

    try {
      const response = await fetchImpl(request.url, {
        method: request.method,
        headers: request.headers,
        body: request.body ?? undefined,
        signal: controller.signal
      });

      const headers: Record<string, string> = {};
      // RN 的 Headers 类型不完整（无 lib.dom），显式标注参数避免跨端隐式 any
      response.headers.forEach((value: string, key: string) => {
        headers[key.toLowerCase()] = value;
      });

      const text = await response.text();
      let body: unknown = null;
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          body = text;
        }
      }

      return { status: response.status, headers, body };
    } catch (err) {
      // 超时要给出可识别的错误类型：否则调用方只看到 AbortError，
      // 分不清是"用户取消"还是"网络超时"，也就无法决定入离线队列还是放弃。
      if (timedOut) throw new RequestTimeoutError(timeoutMs);
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
      request.signal?.removeEventListener('abort', forwardAbort);
    }
  };

  return { send };
}
